import React from "react";
import { CheckCircle, LockClosed } from "../../../../components/icons/svg/outline";

/**
 * TicketStatusSection Component
 * Displays and allows toggling of ticket status (submitted/locked)
 * 
 * @param {Object} data - Form data object with status fields
 * @param {Function} onToggleSubmit - Callback when submit status is toggled
 * @param {Function} onToggleLock - Callback when lock status is toggled
 * @param {boolean} readOnly - Whether the section is read-only
 * @param {boolean} showSubmit - Whether to show submit toggle (default: true)
 * @param {boolean} showLock - Whether to show lock toggle (default: true)
 * @param {string} submitLabel - Label for submit status (default: "Submitted")
 * @param {string} lockLabel - Label for lock status (default: "Locked")
 */
const TicketStatusSection = ({
  data,
  onToggleSubmit,
  onToggleLock,
  readOnly = false,
  showSubmit = true,
  showLock = true,
  submitLabel = "Submitted",
  lockLabel = "Locked",
}) => {
  const isSubmitted = data?.submitted || false;
  const isLocked = data?.locked || false;

  return (
    <div className="flex items-center gap-4">
      {showSubmit && (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">{submitLabel}:</span>
          {readOnly ? (
            <span
              className={`px-2 py-1 rounded text-xs font-medium ${
                isSubmitted
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {isSubmitted ? "Yes" : "No"}
            </span>
          ) : (
            <button
              type="button"
              onClick={onToggleSubmit}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                isSubmitted
                  ? "bg-green-100 text-green-800 hover:bg-green-200"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              <CheckCircle className="w-4 h-4" />
              {isSubmitted ? "Submitted" : "Not Submitted"}
            </button>
          )}
        </div>
      )}

      {showLock && (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">{lockLabel}:</span>
          {readOnly ? (
            <span
              className={`px-2 py-1 rounded text-xs font-medium ${
                isLocked
                  ? "bg-amber-100 text-amber-800"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {isLocked ? "Yes" : "No"}
            </span>
          ) : (
            <button
              type="button"
              onClick={onToggleLock}
              disabled={!isSubmitted}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                isLocked
                  ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                  : isSubmitted
                    ? "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    : "bg-gray-50 text-gray-400 cursor-not-allowed"
              }`}
              title={!isSubmitted ? "Submit first before locking" : ""}
            >
              <LockClosed className="w-4 h-4" />
              {isLocked ? "Locked" : "Not Locked"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TicketStatusSection;
